import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type MergeFieldOption = {
  key: string;
  label: string;
  token: string;
  category: 'contact' | 'event' | 'campaign';
};

type MergeFieldContext = {
  startIndex: number;
  endIndex: number;
  query: string;
};

type MergeFieldInputProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder: string;
  className: string;
  options: MergeFieldOption[];
};

const getMergeFieldContext = (
  value: string,
  caretPosition: number
): MergeFieldContext | null => {
  const safeCaret = Math.max(0, Math.min(caretPosition, value.length));
  const uptoCaret = value.slice(0, safeCaret);
  const startIndex = uptoCaret.lastIndexOf('{{');
  if (startIndex === -1) return null;

  const between = value.slice(startIndex + 2, safeCaret);
  if (between.includes('}}')) return null;

  return {
    startIndex,
    endIndex: safeCaret,
    query: between.trim().toLowerCase(),
  };
};

const MergeFieldInput: React.FC<MergeFieldInputProps> = ({
  value,
  onChange,
  onBlur,
  placeholder,
  className,
  options,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const mergeContext = useMemo(() => {
    const input = inputRef.current;
    if (!input) return null;
    return getMergeFieldContext(value, input.selectionStart ?? value.length);
  }, [value]);

  const filteredOptions = useMemo(() => {
    if (!mergeContext) return [];
    const query = mergeContext.query;
    if (!query) return options;
    return options.filter((option) => {
      const haystack =
        `${option.label} ${option.token} ${option.category}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [mergeContext, options]);

  useEffect(() => {
    const shouldOpen = Boolean(mergeContext);
    setIsOpen(shouldOpen);
    setActiveIndex(0);
  }, [mergeContext]);

  useEffect(() => {
    if (!isOpen || !inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 6,
      left: rect.left,
      width: rect.width,
    });
  }, [isOpen, value, filteredOptions.length]);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (inputRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    const handleWindowChange = () => {
      if (!inputRef.current) return;
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 6,
        left: rect.left,
        width: rect.width,
      });
    };

    document.addEventListener('mousedown', handleOutsideClick);
    window.addEventListener('resize', handleWindowChange);
    window.addEventListener('scroll', handleWindowChange, true);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      window.removeEventListener('resize', handleWindowChange);
      window.removeEventListener('scroll', handleWindowChange, true);
    };
  }, [isOpen]);

  const applyMergeToken = (token: string) => {
    const input = inputRef.current;
    if (!input) return;
    const context = getMergeFieldContext(
      value,
      input.selectionStart ?? value.length
    );
    if (!context) return;

    const nextValue =
      value.slice(0, context.startIndex) +
      `{{${token}}}` +
      value.slice(context.endIndex);
    onChange(nextValue);
    setIsOpen(false);

    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      const cursor = context.startIndex + token.length + 4;
      inputRef.current.focus();
      inputRef.current.setSelectionRange(cursor, cursor);
    });
  };

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (!isOpen || filteredOptions.length === 0) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((prev) => (prev + 1) % filteredOptions.length);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(
              (prev) =>
                (prev - 1 + filteredOptions.length) % filteredOptions.length
            );
          } else if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            const selectedOption = filteredOptions[activeIndex];
            if (selectedOption) {
              applyMergeToken(selectedOption.token);
            }
          } else if (e.key === 'Escape') {
            setIsOpen(false);
          }
        }}
        onClick={() => {
          const input = inputRef.current;
          if (!input) return;
          setIsOpen(
            Boolean(
              getMergeFieldContext(value, input.selectionStart ?? value.length)
            )
          );
        }}
        onBlur={onBlur}
        placeholder={placeholder}
        className={className}
      />

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="z-[9999] fixed rounded-md border border-border bg-card shadow-lg max-h-64 overflow-y-auto"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <button
                  key={option.key}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    applyMergeToken(option.token);
                  }}
                  className={`w-full px-3 py-2 text-left text-sm border-b last:border-b-0 border-border ${index === activeIndex ? 'bg-muted' : 'hover:bg-muted/70'}`}
                >
                  <span className="block text-foreground">{option.label}</span>
                  <span className="block text-xs text-muted-foreground">
                    {`{{${option.token}}}`}
                  </span>
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                No merge fields found
              </div>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

export default MergeFieldInput;
